import { useState, useEffect, useCallback, useRef } from 'react';

// Automatic currency converter for the day sheet — the SOURCE currency is the
// venue's currency (from the venue lookup), the user picks a value + target
// currency and the rate is computed live via open.er-api.com (no key). Every
// computed conversion is written back to the day-sheet `currency_rate` field,
// e.g. "100 CZK ≈ 4 EUR" — which the daysheet PDF shows as CONVERSION RATE.

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'CZK', 'PLN', 'SEK', 'NOK', 'DKK', 'HUF', 'RON', 'JPY', 'CAD', 'AUD', 'BRL', 'TRY', 'AED', 'ILS', 'KZT', 'CNY', 'INR', 'KRW', 'MXN', 'NZD', 'BGN', 'RSD', 'UAH', 'SGD', 'THB'];

interface Props {
  /** ISO code of the source currency — where the band is that day: the venue's
   * local currency for a show, or the accommodation's for a travel day, e.g. 'CZK'. */
  defaultFrom?: string;
  /** ISO code of the target currency — the band's home currency, e.g. 'CHF'. */
  defaultTo?: string;
  /** Write the computed conversion back to the day sheet (currency_rate). */
  onRateChange: (rate: string) => void;
}

export default function CurrencyConverter({ defaultFrom = 'EUR', defaultTo = 'EUR', onRateChange }: Props) {
  const [from] = useState(defaultFrom || 'EUR');
  const [to, setTo] = useState(defaultTo || 'EUR');
  const [amount, setAmount] = useState('100');
  const [rate, setRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Last emitted rate — prevents write loops when the parent re-renders and
  // hands us a new (or equal) value. Only ACTUAL changes call onRateChange.
  const lastEmitted = useRef<string | null>(null);

  // Un montage peut être démonté pendant que le taux est en vol (navigation,
  // onglet fermé, test qui démonte) : sans ce drapeau, le `finally` écrivait
  // dans un composant mort → « window is not defined » en jsdom et un
  // setState-after-unmount dans le navigateur. Bug RÉEL, pas bruit de test.
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const loadRate = useCallback(async () => {
    if (from === to) { setRate(1); setError(false); return; }
    setLoading(true);
    setError(false);
    try {
      const r = await fetch(`https://open.er-api.com/v6/latest/${from}`);
      if (!r.ok) throw new Error('rate failed');
      const data = await r.json();
      if (!alive.current) return;
      setRate(data?.rates?.[to] ?? null);
    } catch (e) {
      if (!alive.current) return;
      setError(true);
      setRate(null);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { void loadRate(); }, [loadRate]);

  const num = parseFloat(amount) || 0;
  const result = rate != null ? num * rate : null;

  // Write the computed conversion back to the day sheet once we have a rate —
  // but ONLY when the computed string actually changed (guard against loops).
  useEffect(() => {
    if (result != null && !loading && num > 0) {
      const rounded = result.toLocaleString('en-US', { maximumFractionDigits: 2 });
      const next = `${amount.trim()} ${from} ≈ ${rounded} ${to}`;
      if (lastEmitted.current !== next) {
        lastEmitted.current = next;
        onRateChange(next);
      }
    }
  }, [result, loading, num, amount, from, to, onRateChange]);

  return (
    <div style={{
      minHeight: 40, boxSizing: 'border-box', maxWidth: '100%',
      padding: '6px 8px', border: '1px solid var(--border)',
      borderRadius: 8, background: 'var(--card)',
      display: 'flex', alignItems: 'center',
    }}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.9rem', width: '100%', minWidth: 0 }}>
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Converter</span>
        <input
          className="form-input"
          type="number"
          style={{ width: 70, flexShrink: 0, height: 28, padding: '0 6px' }}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <span style={{ whiteSpace: 'nowrap' }}>{from}</span>
        <span style={{ whiteSpace: 'nowrap' }}>→</span>
        <select className="form-input" style={{ width: 72, flexShrink: 0, height: 28, padding: '0 4px' }} value={to} onChange={(e) => setTo(e.target.value)}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {loading && <span style={{ color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>…</span>}
        {error && <span style={{ color: 'var(--danger)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>rate unavailable (offline?)</span>}
        {result != null && !loading && (
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            = {result.toLocaleString(undefined, { maximumFractionDigits: 2 })} {to}
          </span>
        )}
      </div>
    </div>
  );
}
